import {
	ActionBar,
	Button,
	ContentCard,
	InputSwitch,
	STATUS_ENUM,
	Text,
	decoratedAttributes,
	useToggle,
} from '@innovyze/stylovyze';
import { useFormikContext } from 'formik';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useGlobalization } from '@Translations/useGlobalization';
import { addRouteNamespace } from '@Utils/actions';
import {
	EditIntegrationParams,
	IntegrationsStoreState,
	IntegrationType,
	ITPipesSettingsFormValues,
} from '../../types/integration.types';
import {
	createIntegration,
	deleteIntegration,
	updateIntegration,
	validateITPipesApiKey,
} from '../actions/integrations.actions';
import { ITpipesSettingsFooter, Container } from './ITPipesSettings.styles';
import ITPipesSettingsValues from './ITPipesSettingsValues';
import DeleteIntegrationModal from '../arcgis/ArcGisSettings/DeleteIntegrationModal';
import { NamespacedStoreState } from '@Types';
import { moduleNamespace } from '@Store/config.store';

export default function ITPipesSettings() {
	const dispatch = useDispatch();
	const { t } = useGlobalization();
	const history = useHistory();
	const { values, isValid, dirty } =
		useFormikContext<ITPipesSettingsFormValues>();
	const [saveError, setSaveError] = useState<string | null>(null);
	const [isSubmitted, setSubmitted] = useState(false);
	const [isDeleteModalOpen, toggleDeleteModalOpen] = useToggle(false);
	const [isSpinning, setSpinning] = useState(false);
	const itpipesValidationStatus = useSelector(
		(state: NamespacedStoreState) =>
			state[moduleNamespace].integrations.itpipesApiKeyValidation?.status,
	);
	const handleCancel = () => history.push(addRouteNamespace('integrations'));
	const handleDelete = () => dispatch(deleteIntegration(values._id ?? ''));
	const isEditMode = values._id != null;
	const isCreateMode = !isEditMode;

	React.useEffect(() => {
		if (!isSubmitted) return;
		if (!isSpinning) setSpinning(true);

		if (itpipesValidationStatus === STATUS_ENUM.SUCCESS) {
			let integration: EditIntegrationParams = {
				_id: values._id,
				name: values.name,
				type: 'itpipes',
				description: values.description ?? 'ITPipes Integration',
				createdAt: new Date(),
				lastModifiedTimestamp: new Date(),
				isValid: true,
				apiKey: values.apiKey,
				portalUrl: values.portalUrl,
			};
			if (values.integrationInfo) {
				integration = {
					...integration,
					isEnabled: values.integrationInfo.toBeEnabled ?? false,
					ssl: values.ssl ?? false,
					isValid: true,
				};
			}
			if (values.integrationTokenInfo) {
				integration = {
					...integration,
					organizationId: values.integrationTokenInfo.organizationId,
					refreshTokenExpires:
						values.integrationTokenInfo.refreshTokenExpires,
					tokenExpires: values.integrationTokenInfo.tokenExpires,
					clientId: values.integrationTokenInfo.clientId,
					username: values.integrationTokenInfo?.username,
					token: values.integrationTokenInfo.token,
					refreshToken: values.integrationTokenInfo.refreshToken,
					portal: values.integrationTokenInfo?.portal,
					isValid: true,
					isUKRegion: values.integrationTokenInfo?.isUKRegion,
				};
			}
			if (isEditMode) {
				dispatch(updateIntegration(integration));
			}
			if (isCreateMode) {
				dispatch(createIntegration(integration));
			}
			setSubmitted(false);
			setSpinning(false);
		}
		if (itpipesValidationStatus === STATUS_ENUM.ERROR) {
			setSaveError(
				t('API Key and Portal URL are invalid, please check.'),
			);
			setSubmitted(false);
			setSpinning(false);
		}
	}, [itpipesValidationStatus, isSubmitted]);

	const adpAttrs = decoratedAttributes({
		name: values.name ?? '',
		description: values.description ?? '',
		type: IntegrationType.ITPipes,
	});
	const handleSaveIntegration = () => {
		setSubmitted(true);
		setSaveError(null);
		setSpinning(true);
		if (values.name) {
			dispatch(
				validateITPipesApiKey({
					portalUrl: values.portalUrl || '',
					apiKey: values.apiKey || '',
				}),
			);
		}
	};

	React.useEffect(() => {
		document.body.style.cursor = isSpinning ? 'wait' : 'default';
		return () => {
			document.body.style.cursor = 'default';
		};
	}, [isSpinning]);

	return (
		<ContentCard
			header={
				<ActionBar>
					<Text variant="heading-medium-strong">
						{t('ITPipes Config')}
					</Text>
					<InputSwitch
						fieldAttrs={{ name: 'integrationInfo.toBeEnabled' }}
						switchProps={{}}
						label={
							values.integrationInfo?.toBeEnabled
								? t('Enabled')
								: t('Disabled')
						}
					/>
				</ActionBar>
			}
			footer={
				<ActionBar
					style={{
						justifyContent: isEditMode
							? 'space-between'
							: 'flex-end',
					}}>
					{isEditMode && (
						<>
							<DeleteIntegrationModal
								open={isDeleteModalOpen}
								onCancel={toggleDeleteModalOpen}
								onDelete={handleDelete}
							/>
							<Button
								dataCy="delete-integration"
								variant="contained"
								color="error"
								onClick={toggleDeleteModalOpen}>
								{t('Delete')}
							</Button>
						</>
					)}
					<ITpipesSettingsFooter>
						<Button
							dataCy="cancel-integration"
							variant="contained"
							color="secondary"
							onClick={handleCancel}>
							{t('Cancel')}
						</Button>

						<Button
							adp-click="true"
							dataCy="save-integration"
							variant="contained"
							color="primary"
							disabled={!isValid || !dirty || isSubmitted}
							onClick={handleSaveIntegration}
							{...adpAttrs}>
							{t('Save')}
						</Button>
						{saveError && (
							<div style={{ color: 'red', marginTop: 8 }}>
								{saveError}
							</div>
						)}
					</ITpipesSettingsFooter>
				</ActionBar>
			}
			applyHeaderDivider>
			<Container>
				<ITPipesSettingsValues />
			</Container>
		</ContentCard>
	);
}
